import { calcNbrMonthsBtwnDates } from "../../../objects/CommonUse/nbr_months_btwn_dates_calc";
import {paymentHistoryCreditScore} from "./payment_history_credit_score";

export const determineCreditScore = (creditInfo, loanTable, gameDate, gameNbrMonths, timeWizardInfo) => {

    /*
        Step 1: - calculate values for each factor
            Payment History	    35 pts	% of payments on time
            Credit Utilization	30 pts	Score decreases as utilization rises
            Length of History	15 pts	Longer average age = more points
            Credit Mix	        10 pts	More account types = more points
            New Credit	        10 pts	Fewer recent inquiries = more points
        Step 2 — Combine Into a 0–100 Score
            FinancialScore = PH + CU + LH + CM + NC   (max 100)
        Step 3 — Convert to FICO-like Range (300–850)
            CreditScore = 300 + (FinancialScore / 100) × 550
            Example:
            If a player scores 78/100:
            CreditScore = 300 + 0.78 × 550 ≈ 727
    */

    let payHistoryPoints = 35;
    if (creditInfo.paymentHistory && creditInfo.paymentHistory.length > 0){
        // payment history - nbr of payments on time / total number of payments (7 years)
        payHistoryPoints = paymentHistoryCreditScore(creditInfo.paymentHistory);
    }else{
        if (loanTable.length > 0){  
            let totalGameMonths = 360;
            if (timeWizardInfo.gameID){
                totalGameMonths = timeWizardInfo.gameNbrYears * 12;
            }
            const nbrMonthsPlayed = totalGameMonths - gameNbrMonths;
            if (nbrMonthsPlayed < 6){
                payHistoryPoints = +(35 * nbrMonthsPlayed / 6).toFixed(0)
            }
        }else{
            // player has no credit card and no loans - set score to 25 so having
            // no credit and loans is not a punishment re credit sccore
            payHistoryPoints = +25;
        }
    }
    let mostRecentLatePayment = 0;   // nbr months since most recent late payment
    let nbrMonths = 1;
    if (timeWizardInfo.monthsPerTurn &&
        timeWizardInfo.status === "active"){
            nbrMonths = timeWizardInfo.monthsPerTurn;
    }
    if (creditInfo.paymentHistory){
        const sliceValue = nbrMonths * -1;
        const latePayments = creditInfo.paymentHistory
            .slice(0, sliceValue) // exclude last entries
            .filter(item => item.payStatus === "paidLate" || item.payStatus === "unpaid");
        if (latePayments.length > 0){
            let latePaymentDate = "0000-00";
            for (let i=0; i<latePayments.length; i++){
                if (latePayments[i].month > latePaymentDate){
                    latePaymentDate = latePayments[i].month;
                }
                mostRecentLatePayment = calcNbrMonthsBtwnDates(latePaymentDate, gameDate);
            }
        }
    }
   
    // credit utilization - how much of available credit used  outstanding balance / credit limit
    // if there is no credit card, then student gets 'partial credit' for credit utilization
    let creditUtilization = 30;
    if (creditInfo.gameID){
        creditUtilization = (creditInfo.prevPeriodOutstandBalance / creditInfo.creditMaxAmount) * 100;
    }
    let creditUtilPoints = 0;
    switch (true){
        case creditUtilization < 10:
            creditUtilPoints = 30;
            break;
        case creditUtilization >= 10 && creditUtilization < 30:
            creditUtilPoints = 25;
            break;
        case creditUtilization >=30 && creditUtilization < 50:
            creditUtilPoints = 15;
            break;
        case creditUtilization >= 50 && creditUtilization < 75:
            creditUtilPoints = 5;
            break;
        default:
            creditUtilPoints = 0;

    }
    
    // length of credit history - how long (nbr years) have had credit card or loans.
    // find oldest credit item
    let oldestCreditDate = "9999/99";
    let totalCreditAge = 0;
    let creditNumber = 0;
    if (creditInfo.currentlyActive && creditInfo.currentlyActive === true){
        if (creditInfo.paymentHistory.length > 0){
            oldestCreditDate = creditInfo.paymentHistory[0].month;
        }else{
            oldestCreditDate = creditInfo.creditCardStartDate;
        }
        const creditCardAge = calcNbrMonthsBtwnDates(creditInfo.creditCardStartDate, gameDate);
        totalCreditAge += creditCardAge;
        creditNumber += 1;
    }
    for (let i=0; i<loanTable.length; i++){
        if (loanTable[i].loanType === "Mortgage Insurance (PMI)"){
            //   skip PMI for credit score
        }else{
            if (loanTable[i].loanNbrMonths > 0){ 
                if (loanTable[i].loanStartDate < oldestCreditDate ){
                    oldestCreditDate = loanTable[i].loanStartDate;
                }
                const loanAge = calcNbrMonthsBtwnDates(loanTable[i].loanStartDate, gameDate);
                totalCreditAge += loanAge;
                creditNumber += 1;            
            }
        }
    }
    const creditHistoryMonths = calcNbrMonthsBtwnDates(oldestCreditDate, gameDate);
    let oldestAgePoints = 0;
    switch (true) {
    case creditHistoryMonths <= 6:
        oldestAgePoints = 0;
        break;
    case creditHistoryMonths <= 12:
        oldestAgePoints = 3;
        break;
    case creditHistoryMonths <= 24:
        oldestAgePoints = 6;
        break;
    case creditHistoryMonths <= 48:
        oldestAgePoints = 9;
        break;
    default: // 4+ years
        oldestAgePoints = 12;
        break;
}

    const creditHistoryAvg = totalCreditAge / creditNumber;
    const creditHistoryPercent = (creditHistoryAvg / creditHistoryMonths) * 100;
    let avgAgePoints = 0;
    switch (true){
        case creditHistoryPercent >= 70:
            avgAgePoints = 3;
            break;
        case creditHistoryPercent >= 40:
            avgAgePoints = 2;
            break;
        case creditHistoryPercent >= 20:
            avgAgePoints = 1;
            break;
       default:
            avgAgePoints = 0;
            break;
    }
    const creditHistoryPoints = oldestAgePoints + avgAgePoints;

    // credit mix - credit card counts as '1' and loans (installment debt) count as '1' - max is '2'
    let creditMixPoints = 0;
    let creditMixCount = 0;
    if (creditInfo.currentlyActive && creditInfo.currentlyActive === true){
          creditMixCount += 1;
    }
    for (let i=0; i<loanTable.length; i++){
        if (loanTable[i].loanType === "Mortgage Insurance (PMI)"){
            //   skip PMI for credit score
        }else{
            if (loanTable[i].loanNbrMonths > 0){
                creditMixCount += 1;
                break;
            }
        }
    }
    if (creditMixCount === 1){
         creditMixPoints = 5;
    }else{
        if (creditMixCount === 2){
            creditMixPoints = 10;
        }
    }

    // new credit - new loans or new credit card 
    let newCreditPoints = 0;
    let newCreditCount = 0;
    let newCreditArr = [];
    if (creditInfo.currentlyActive && creditInfo.currentlyActive === true){
        const creditCardMonths = calcNbrMonthsBtwnDates(creditInfo.creditCardStartDate, gameDate);
        if (creditCardMonths <= 6){
            newCreditCount += 1;
            const newCreditObj = {label: "Credit Card Application", date: creditInfo.creditCardStartDate};
            newCreditArr.push(newCreditObj);

        }
    }
    for (let i=0; i<loanTable.length; i++){
        if (loanTable[i].loanType === "Mortgage Insurance (PMI)"){
            //   skip PMI for credit score
        }else{
            if (loanTable[i].loanNbrMonths > 0){
                const loanMonths = calcNbrMonthsBtwnDates(loanTable[i].loanStartDate, gameDate);
                if (loanMonths <= 6){
                    newCreditCount += 1;
                    const newCreditObj = {label: loanTable[i].loanType, date: loanTable[i].loanStartDate};
                    newCreditArr.push(newCreditObj);
                }
            }
        }
    }
    switch (true){
        case newCreditCount === 0:
            newCreditPoints = 10;
            break;
        case newCreditCount > 0 && newCreditCount <= 1:
            newCreditPoints = 8;
            break;
        default:
            newCreditPoints = 4;
            break;
    }
     
    const totalInitialScore = payHistoryPoints + creditUtilPoints + creditHistoryPoints +
        creditMixPoints + newCreditPoints;
    let creditScore = +(300 + ((totalInitialScore / 100) * 550)).toFixed(0);
    if (payHistoryPoints < 20) {
        creditScore = Math.min(creditScore, 710);
    }

    let creditReportFactorArr = [];
    let creditReportFactorObj = {code: "PH", score: payHistoryPoints};
    creditReportFactorArr.push(creditReportFactorObj);
    creditReportFactorObj = {code: "CU", score: creditUtilPoints};
    creditReportFactorArr.push(creditReportFactorObj);
    creditReportFactorObj = {code: "LC", score: creditHistoryPoints};
    creditReportFactorArr.push(creditReportFactorObj);
     creditReportFactorObj = {code: "CM", score: creditMixPoints};
    creditReportFactorArr.push(creditReportFactorObj);
     creditReportFactorObj = {code: "NC", score: newCreditPoints};
    creditReportFactorArr.push(creditReportFactorObj);

    const creditReport = {creditReportDate: gameDate,
        creditFactorArr: creditReportFactorArr,
        newCreditArr: newCreditArr,
        creditScore: creditScore,
        creditHistoryMonths: creditHistoryMonths,
        creditUtilization: creditUtilization,
        mostRecentLatePayment: mostRecentLatePayment
    }
    return creditReport;

}
export default determineCreditScore;